/**
 * 설비 관련 GraphQL 쿼리 및 뮤테이션
 * 웹(@ict/web)과 동일한 리졸버 사용
 */

import { gql } from "@apollo/client";
import { MEASURES_FIELDS_FRAGMENT, type MachineMeasuresData } from "./measures";
import {
  EquipmentCategory,
  EQUIPMENT_CATEGORY_ORDER,
  getEquipmentCategoryLabel,
  groupEquipmentsByCategory,
  MACHINE_KEYS,
  MACHINE_TITLES,
  MACHINE_META,
} from "@ict/shared";

// ============================================
// @ict/shared에서 re-export (하위 호환성)
// ============================================
export {
  EquipmentCategory,
  EQUIPMENT_CATEGORY_ORDER,
  getEquipmentCategoryLabel,
  groupEquipmentsByCategory,
};
export type EquipmentCategoryType = EquipmentCategory;
export const CATEGORY_ORDER = EQUIPMENT_CATEGORY_ORDER;

/** 설비 타입 정의 */
export interface EquipmentType {
  key: string;
  title: string;
  category: EquipmentCategory;
  ratio: number;
  unit: string;
}

/** 설비 현황 아이템 */
export interface EquipmentStatusItem {
  key: string;
  title: string;
  category?: EquipmentCategory;
  totalCount: number;
  checkCount: number;
  docsCount: number;
  docsIds?: string[];
  ratio?: number;
  unit?: string;
}

// ============================================
// Query: 설비 타입 목록 조회 (34개)
// ============================================
export const EQUIPMENT_TYPES_QUERY = gql`
  query EquipmentTypes {
    equipmentTypes {
      key
      title
      category
      ratio
      unit
    }
  }
`;

export interface EquipmentTypesData {
  equipmentTypes: EquipmentType[];
}

/**
 * 설비 타입 목록 (상수 기반)
 * 34개 설비 타입의 메타데이터를 @ict/shared 상수에서 생성합니다.
 * 오프라인에서도 항상 사용 가능합니다.
 */
const STATIC_EQUIPMENT_TYPES: EquipmentType[] = MACHINE_KEYS.map((key) => ({
  key,
  title: MACHINE_TITLES[key],
  category: MACHINE_META[key].category,
  ratio: MACHINE_META[key].ratio,
  unit: MACHINE_META[key].unit,
}));

/**
 * 설비 타입 목록 조회 hook
 * 34개 설비 타입의 메타데이터를 반환합니다.
 * 상수 기반으로 오프라인에서도 항상 동작합니다.
 */
export function useEquipmentTypes(): {
  data: EquipmentTypesData;
  loading: false;
  error: undefined;
} {
  // 상수 기반이므로 항상 즉시 반환
  return {
    data: { equipmentTypes: STATIC_EQUIPMENT_TYPES },
    loading: false,
    error: undefined,
  };
}

/**
 * 설비 생성 (MachineBase 템플릿 기반)
 * Equipment.resolver.ts의 createEquipment mutation 사용
 * 웹과 동일한 방식 (개수 입력)
 */
export const CREATE_EQUIPMENT_MUTATION = gql`
  mutation CreateEquipment($input: CreateEquipmentInput!) {
    createEquipment(input: $input) {
      success
      equipments {
        id
        title
      }
      checkCount
    }
  }
`;

/**
 * 설비 삭제 (Hard Delete)
 * Equipment.resolver.ts의 deleteEquipment mutation 사용
 */
export const DELETE_EQUIPMENT_MUTATION = gql`
  mutation DeleteEquipment($input: DeleteEquipmentInput!) {
    deleteEquipment(input: $input) {
      success
      deletedId
      remainingCount
      checkCount
    }
  }
`;

/**
 * 설비 문서 목록 조회 (설비 그룹 내 모든 설비)
 * MachineDocument.resolver.ts의 machineDocuments query 사용
 */
export const MACHINE_DOCUMENTS_QUERY = gql`
  query MachineDocuments($projectId: ID!, $docKey: String!) {
    machineDocuments(projectId: $projectId, docKey: $docKey) {
      id
      title
      position
      modelNo
      modelYear
      modelCreator
    }
  }
`;

/**
 * 설비 문서 조회 (점검 데이터 + 프로젝트 정보 포함)
 * MachineDocument.resolver.ts의 machineDocument query 사용
 * Project.resolver.ts의 project query 사용 (프로젝트명 조회)
 */
/**
 * 문서 버전만 조회 (동기화 충돌 해결용)
 */
export const MACHINE_DOCUMENT_VERSION_QUERY = gql`
  query MachineDocumentVersion($projectId: ID!, $docKey: String!, $machineId: ID!) {
    machineDocument(projectId: $projectId, docKey: $docKey, machineId: $machineId) {
      id
      version
    }
  }
`;

/** 사진대지 동기화용 pictures + version 조회 */
export const MACHINE_DOCUMENT_PICTURES_QUERY = gql`
  query MachineDocumentPictures($projectId: ID!, $docKey: String!, $machineId: ID!) {
    machineDocument(projectId: $projectId, docKey: $docKey, machineId: $machineId) {
      id
      pictures
      version
    }
  }
`;

export const MACHINE_DOCUMENT_QUERY = gql`
  query MachineDocument($projectId: ID!, $docKey: String!, $machineId: ID) {
    project(id: $projectId) {
      _id
      name
      number
      type
      checkManager
      startDate
      endDate
    }
    machineDocument(projectId: $projectId, docKey: $docKey, machineId: $machineId) {
      id
      title
      position
      modelNo
      modelYear
      modelCreator
      checkName
      checkStartDate
      checkEndDate
      checkList {
        id
        title
        result
        image
        imageO
        imageX
        underReason
        underSolution
        group
      }
      maintenance {
        id
        title
        result
        image
        imageO
        imageX
        underReason
        underSolution
        group
      }
      dataList: data {
        id
        section
        title
        unit
        designValue
        checkValue
      }
      pictures
      version
    }
  }
`;

// 타입 정의
export interface CreateEquipmentInput {
  projectId: string;
  machineKey: string;
  count: number;
}

export interface DeleteEquipmentInput {
  projectId: string;
  machineKey: string;
  machineId: string;
}

export interface MachineDocumentQueryVariables {
  projectId: string;
  docKey: string;
  machineId?: string;
}

/** 프로젝트 정보 (MACHINE_DOCUMENT_QUERY 응답) */
export interface ProjectInfo {
  _id: string;
  name: string;
  number?: string;
  type: string; // "PERFORMANCE_CHECK" | "MAINTENANCE"
  checkManager?: string;
  startDate?: string;
  endDate?: string;
}

/** 점검항목 타입 */
export interface CheckItem {
  id: string;
  title: string;
  result?: string;
  image?: string;
  imageO?: string;
  imageX?: string;
  underReason?: string;
  underSolution?: string;
  group?: string;
}

/** 데이터 항목 타입 */
export interface DataItem {
  id: string;
  section: string;
  title: string;
  unit?: string;
  designValue?: string;
  checkValue?: string;
}

/** 설비 문서 타입 */
export interface MachineDocument {
  id: string;
  title: string;
  position?: string;
  modelNo?: string;
  modelYear?: string;
  modelCreator?: string;
  checkName?: string;
  checkStartDate?: string;
  checkEndDate?: string;
  checkList?: CheckItem[];
  maintenance?: CheckItem[];
  data?: DataItem[];
  pictures?: string[];
  version: number;
}

/** MACHINE_DOCUMENT_QUERY 응답 타입 */
export interface MachineDocumentQueryData {
  project: ProjectInfo | null;
  machineDocument: MachineDocument | null;
}

// ============================================
// Mutation: 사진대지 업데이트
// ============================================

/**
 * 설비 문서 사진대지 업데이트
 * MachineDocument.resolver.ts의 updateMachineDocumentPictures mutation 사용
 */
export const UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION = gql`
  mutation UpdateMachineDocumentPictures($input: UpdateMachineDocumentPicturesInput!) {
    updateMachineDocumentPictures(input: $input) {
      success
      error
      errorCode
      machineDocument {
        id
        title
        pictures
        version
      }
      newVersion
      clientMutationId
    }
  }
`;

export interface UpdateMachineDocumentPicturesInput {
  projectId: string;
  docKey: string;
  machineId: string;
  pictures: string[];
  expectedVersion: number;
  clientMutationId?: string;
}

export interface UpdateMachineDocumentPicturesResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
  machineDocument?: {
    id: string;
    title: string;
    pictures: string[];
    version: number;
  };
  newVersion?: number;
  clientMutationId?: string;
}

export interface UpdateMachineDocumentPicturesData {
  updateMachineDocumentPictures: UpdateMachineDocumentPicturesResponse;
}

export interface UpdateMachineDocumentPicturesVariables {
  input: UpdateMachineDocumentPicturesInput;
}

/** clientMutationId 생성 헬퍼 */
export function generateClientMutationId(): string {
  return `mobile-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Mutation: 보유대수/점검수량 수정
// ============================================

/**
 * 설비 보유대수/점검수량 수정
 * Equipment.resolver.ts의 updateEquipmentCount mutation 사용
 */
export const UPDATE_EQUIPMENT_COUNT_MUTATION = gql`
  mutation UpdateEquipmentCount($input: UpdateEquipmentCountInput!) {
    updateEquipmentCount(input: $input) {
      success
      equipment {
        key
        title
        totalCount
        checkCount
        ratio
        unit
      }
      error
    }
  }
`;

export interface UpdateEquipmentCountInput {
  projectId: string;
  equipmentKey: string;
  totalCount: number;
  checkCount?: number;
}

export interface UpdateEquipmentCountResponse {
  success: boolean;
  equipment?: {
    key: string;
    title: string;
    totalCount: number;
    checkCount: number;
    ratio: number;
    unit: string;
  };
  error?: string;
}

export interface UpdateEquipmentCountData {
  updateEquipmentCount: UpdateEquipmentCountResponse;
}

export interface UpdateEquipmentCountVariables {
  input: UpdateEquipmentCountInput;
}

// ============================================
// Query: 프로젝트 전체 설비 데이터 조회 (오프라인 동기화용)
// ============================================

/**
 * 프로젝트 하위 모든 설비 데이터를 한 번에 조회
 * 오프라인 동기화를 위해 IndexedDB에 저장할 때 사용
 */
export const PROJECT_MACHINE_GROUPS_QUERY = gql`
  query ProjectMachineGroups($projectId: ID!) {
    projectMachineGroups(projectId: $projectId) {
      projectId
      projectName
      projectType
      measureDiscus {
        key
        common
        good
        caution
        bad
        na
      }
      machineGroups {
        key
        totalCount
        checkCount
        machines {
          id
          title
          position
          modelNo
          modelYear
          modelCreator
          checkList {
            id
            title
            result
            image
            imageO
            imageX
            underReason
            underSolution
            group
          }
          maintenance {
            id
            title
            result
            image
            imageO
            imageX
            underReason
            underSolution
            group
          }
          data {
            id
            section
            title
            unit
            designValue
            checkValue
          }
          pictures
          version
          measures {
            ...MeasuresFields
          }
        }
      }
    }
  }
  ${MEASURES_FIELDS_FRAGMENT}
`;

/** 설비 그룹 내 개별 설비 (동기화용) */
export interface ProjectMachineGroupMachine {
  id: string;
  title: string;
  position?: string;
  modelNo?: string;
  modelYear?: string;
  modelCreator?: string;
  checkList?: CheckItem[];
  maintenance?: CheckItem[];
  data?: DataItem[];
  pictures?: string[];
  version: number;
  measures?: MachineMeasuresData["machineMeasures"];
}

/** 설비 그룹 (동기화용) */
export interface ProjectMachineGroup {
  key: string;
  totalCount: number;
  checkCount: number;
  machines: ProjectMachineGroupMachine[];
}

// @ict/shared에서 SSOT import + re-export
import type { MeasureDiscusItem } from "@ict/shared";
export type { MeasureDiscusItem } from "@ict/shared";

/** projectMachineGroups 쿼리 응답 */
export interface ProjectMachineGroupsData {
  projectMachineGroups: {
    projectId: string;
    projectName: string;
    projectType: string; // "PERFORMANCE_CHECK" | "MAINTENANCE"
    machineGroups: ProjectMachineGroup[];
    measureDiscus?: MeasureDiscusItem[];
  };
}

export interface ProjectMachineGroupsVariables {
  projectId: string;
}

// ============================================
// Query: 설비 템플릿 목록 조회 (오프라인 캐싱용)
// Feature 288: 오프라인 설비 문서 생성 지원
// ============================================

/**
 * 설비 템플릿 목록 조회 (인증만 필요, 권한 체크 없음)
 * MachineBase.resolver.ts의 machineTemplates query 사용
 */
export const MACHINE_TEMPLATES_QUERY = gql`
  query MachineTemplates {
    machineTemplates {
      id
      key
      sectionTitle
      checkListTemplate {
        title
        result
        group
        syncId
        inspectionMethod
        inspectionMethodImages
        inspectionCriteria
        compliantTemplate
        nonCompliantTemplate
      }
      maintenanceTemplate {
        title
        result
        group
        syncId
        inspectionMethod
        inspectionMethodImages
        inspectionCriteria
        compliantTemplate
        nonCompliantTemplate
      }
      dataTemplate {
        section
        title
        unit
        calKey
        enableInputDesignValue
        enableInputCheckValue
        bgColor
      }
      docFormat {
        check
        abnormal
        photoHeight
        etc
      }
      docFormatMT {
        check
        abnormal
        photoHeight
        etc
      }
      howToWrite
      howToWriteMT
      etcContent
    }
  }
`;

/** 점검항목 템플릿 타입 (API 응답) */
export interface CheckItemTemplateResponse {
  title: string;
  result: string;
  group: string;
  syncId?: string;
  inspectionMethod?: string[];
  inspectionMethodImages?: string[];
  inspectionCriteria?: string[];
  compliantTemplate?: string;
  nonCompliantTemplate?: string;
}

/** 데이터 항목 템플릿 타입 (API 응답) */
export interface DataItemTemplateResponse {
  section: string;
  title: string;
  unit?: string;
  calKey?: string;
  enableInputDesignValue: string;
  enableInputCheckValue: string;
  bgColor?: string;
}

/** 문서 포맷 타입 (API 응답) */
export interface DocFormatResponse {
  check?: string;
  abnormal?: string;
  photoHeight?: string;
  etc?: string;
}

/** 설비 템플릿 타입 (API 응답) */
export interface MachineTemplateResponse {
  id: string;
  key: string;
  sectionTitle: string;
  checkListTemplate: CheckItemTemplateResponse[];
  maintenanceTemplate: CheckItemTemplateResponse[];
  dataTemplate: DataItemTemplateResponse[];
  docFormat?: DocFormatResponse;
  docFormatMT?: DocFormatResponse;
  howToWrite?: string;
  howToWriteMT?: string;
  etcContent?: string;
}

/** machineTemplates 쿼리 응답 */
export interface MachineTemplatesData {
  machineTemplates: MachineTemplateResponse[];
}

/** machineTemplates 쿼리 변수 (없음) */
export type MachineTemplatesVariables = Record<string, never>;

// ============================================
// 공유 유틸리티: Equipment delete mutation 실행
// ============================================

/**
 * Equipment delete mutation 실행 헬퍼 (DRY)
 * SyncQueueContext와 useAutoSync에서 공통 사용
 */
export async function executeEquipmentDeleteMutation(
  input: Record<string, unknown>,
  deleteMutationFn: (opts: {
    variables: { input: unknown };
  }) => Promise<{ data?: unknown; errors?: readonly { message: string }[] }>
): Promise<{ newVersion?: number }> {
  // equipmentOperation은 서버 전송 전 제거 (라우팅 전용 필드)
  const { equipmentOperation: _op, ...serverInput } = input;
  const result = await deleteMutationFn({ variables: { input: serverInput } });
  if (result.errors) {
    throw new Error(result.errors[0]?.message || "GraphQL error");
  }
  // as any 사유: deleteEquipment 반환 타입의 구체적 필드를 컴파일 타임에 알 수 없음
  const response = (result.data as any)?.deleteEquipment; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (response && !response.success) {
    throw new Error("설비 삭제 실패");
  }
  return {};
}
